import { ITEM_STATUS_LABELS, CHECKLIST_STATUS_LABELS, getCategorias, FOTOS_GERAIS_ITEM_ID } from "./checklistTemplate";
import {
  normalizeAvarias,
  GRAVIDADE_LABELS,
  GRAVIDADE_COLORS,
  getParteLabel,
  normalizarPonto,
  corDaMarcacao,
  FORMAS,
} from "./avariaModel";

const STATUS_COLORS = {
  ok: "#15803d",
  atencao: "#b45309",
  critico: "#b91c1c",
  nao_aplicavel: "#6b7280",
};

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

// Redesenha no documento as marcacoes feitas sobre a foto. Pinos continuam
// sendo os mesmos circulos numerados de sempre; as formas novas saem numa
// camada SVG por cima da imagem. O SVG nao tem viewBox de proposito: com
// coordenadas em "%" e espessura em px, nada distorce em foto deitada ou em
// pe, e a ponta da seta nao estica.
function formaSvg(p, chave) {
  if (p.forma === FORMAS.PIN) return "";
  const cor = corDaMarcacao(p);
  const esp = p.espessura || 4;
  const temFim = typeof p.x2 === "number" && typeof p.y2 === "number";
  const traco = `fill="none" stroke="${cor}" stroke-width="${esp}" stroke-linecap="round"`;

  if (p.forma === FORMAS.SETA && temFim) {
    const mk = `mk-${chave}`;
    return (
      `<defs><marker id="${mk}" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4.5" markerHeight="4.5" ` +
      `orient="auto-start-reverse" markerUnits="strokeWidth"><path d="M 0 0 L 10 5 L 0 10 z" fill="${cor}"/></marker></defs>` +
      `<line x1="${p.x}%" y1="${p.y}%" x2="${p.x2}%" y2="${p.y2}%" marker-end="url(#${mk})" ${traco}/>`
    );
  }
  if (p.forma === FORMAS.RETANGULO && temFim) {
    return (
      `<rect x="${Math.min(p.x, p.x2)}%" y="${Math.min(p.y, p.y2)}%" ` +
      `width="${Math.abs(p.x2 - p.x)}%" height="${Math.abs(p.y2 - p.y)}%" ${traco}/>`
    );
  }
  if (p.forma === FORMAS.ELIPSE && temFim) {
    return (
      `<ellipse cx="${(p.x + p.x2) / 2}%" cy="${(p.y + p.y2) / 2}%" ` +
      `rx="${Math.abs(p.x2 - p.x) / 2}%" ry="${Math.abs(p.y2 - p.y) / 2}%" ${traco}/>`
    );
  }
  if (p.forma === FORMAS.TEXTO) {
    // paint-order="stroke" poe o contorno branco ATRAS do preenchimento: o
    // texto fica legivel tanto em lataria clara quanto escura.
    return (
      `<text x="${p.x}%" y="${p.y}%" fill="${cor}" stroke="#ffffff" stroke-width="${Math.max(esp, 3)}" ` +
      `paint-order="stroke" font-size="${11 + esp * 2}" font-weight="700" dominant-baseline="middle">` +
      `${escapeHtml(p.texto || "")}</text>`
    );
  }
  return "";
}

// Rotulo de cada marcacao na lista de texto abaixo da foto. Pinos mantem a
// MESMA numeracao que aparece desenhada sobre a imagem; as formas entram
// pelo nome, porque elas nao recebem numero no desenho. Sem isto a lista
// diria "2." para uma seta que no desenho nao tem numero nenhum.
function rotuloMarcacao(p, indicePin) {
  if (p.forma === FORMAS.PIN) return `${indicePin}.`;
  if (p.forma === FORMAS.SETA) return "Seta:";
  if (p.forma === FORMAS.RETANGULO) return "Retangulo:";
  if (p.forma === FORMAS.ELIPSE) return "Elipse:";
  if (p.forma === FORMAS.TEXTO) return "Texto:";
  return "-";
}

function listaMarcacoesHtml(foto) {
  const pontos = (foto.pontos || []).map(normalizarPonto);
  if (pontos.length === 0) return "";
  let nPin = 0;
  const itens = pontos
    .map((p) => {
      if (p.forma === FORMAS.PIN) nPin += 1;
      const cor = corDaMarcacao(p);
      const extra = p.forma === FORMAS.TEXTO && p.texto ? ` &middot; "${escapeHtml(p.texto)}"` : "";
      return (
        `<li><span class="avaria-dot" style="background:${cor};border-color:${cor}"></span>` +
        `<strong>${rotuloMarcacao(p, nPin)}</strong> ${escapeHtml(p.tipo)} &mdash; ` +
        `${escapeHtml(GRAVIDADE_LABELS[p.gravidade] || p.gravidade)}${extra}` +
        `${p.observacao ? ` &middot; ${escapeHtml(p.observacao)}` : ""}</li>`
      );
    })
    .join("");
  return `<ul class="avarias-lista avarias-lista--foto">${itens}</ul>`;
}

function marcacoesHtml(foto) {
  const pontos = (foto.pontos || []).map(normalizarPonto);
  const pins = pontos.filter((p) => p.forma === FORMAS.PIN);
  const spans = pins
    .map(
      (p, i) =>
        `<span class="avaria-marker" style="left:${p.x}%;top:${p.y}%;background:${corDaMarcacao(p)};border-color:${corDaMarcacao(p)}">${i + 1}</span>`
    )
    .join("");
  const formas = pontos
    .map((p, i) => formaSvg(p, `${String(foto.id || "f").replace(/[^a-zA-Z0-9_-]/g, "")}-${i}`))
    .filter(Boolean)
    .join("");
  return spans + (formas ? `<svg class="avaria-svg">${formas}</svg>` : "");
}

function formatData(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

// Gera um documento HTML autocontido (sem dependências externas) com o
// registro completo do checklist: dados de cliente/veículo, itens por
// categoria com status/observação/fotos, e assinaturas. Pode ser salvo
// como arquivo .html ou aberto numa aba e impresso/"salvo como PDF" pelo
// próprio navegador.
export function buildChecklistDocument(checklist, { workshopName, logoUrl } = {}) {
  // AUD-B: idem ChecklistForm -- sem isto, o PDF de uma moto sairia sem
  // nenhum item, porque filtraria pelas categorias do template de carro.
  const categorias = getCategorias(checklist.itens);

  const itensHtml = categorias
    .map((categoria) => {
      const itens = checklist.itens.filter((i) => i.categoria === categoria);
      if (itens.length === 0) return "";
      const linhas = itens
        .map((item) => {
          const cor = STATUS_COLORS[item.status] || "#6b7280";
          const fotos = (item.fotos || [])
            .map((foto) => `<img src="${foto}" alt="evidência" class="foto" />`)
            .join("");
          return `
            <tr>
              <td>${escapeHtml(item.nome)}</td>
              <td><span class="status-pill" style="background:${cor};border-color:${cor}">${escapeHtml(
                ITEM_STATUS_LABELS[item.status] || item.status
              )}</span></td>
              <td>${escapeHtml(item.observacao) || "—"}</td>
              <td>${fotos ? `<div class="fotos">${fotos}</div>` : "—"}</td>
            </tr>`;
        })
        .join("");
      return `
        <h3>${escapeHtml(categoria)}</h3>
        <table class="itens">
          <thead>
            <tr><th>Item</th><th>Status</th><th>Observação</th><th>Fotos</th></tr>
          </thead>
          <tbody>${linhas}</tbody>
        </table>`;
    })
    .join("");

  const fotosGeraisItem = checklist.itens.find((i) => i.id === FOTOS_GERAIS_ITEM_ID);
  const fotosGeraisHtml =
    fotosGeraisItem && fotosGeraisItem.fotos.length > 0
      ? `
        <h3>Fotos gerais do veículo</h3>
        <div class="fotos">
          ${fotosGeraisItem.fotos
            .map((foto) => `<img src="${foto}" alt="evidência geral" class="foto" />`)
            .join("")}
        </div>`
      : "";

  const avarias = normalizeAvarias(checklist.avarias);

  const diagramaHtml =
    avarias.diagrama.length > 0
      ? `
        <h4>Avarias por peça (diagrama técnico)</h4>
        <ul class="avarias-lista">
          ${avarias.diagrama
            .map(
              (r) => `
            <li>
              <span class="avaria-dot" style="background:${GRAVIDADE_COLORS[r.gravidade] || "#6b7280"};border-color:${GRAVIDADE_COLORS[r.gravidade] || "#6b7280"}"></span>
              <strong>${escapeHtml(getParteLabel(r.parteId))}:</strong> ${escapeHtml(r.tipo)} —
              ${escapeHtml(GRAVIDADE_LABELS[r.gravidade] || r.gravidade)}
              ${r.comentario ? `· ${escapeHtml(r.comentario)}` : ""}
              ${
                r.fotos && r.fotos.length > 0
                  ? `<div class="fotos">${r.fotos
                      .map((foto) => `<img src="${foto}" alt="evidência" class="foto" />`)
                      .join("")}</div>`
                  : ""
              }
            </li>`
            )
            .join("")}
        </ul>`
      : "";

  const fotosAvariaHtml =
    avarias.fotos.length > 0
      ? `
        <h4>Fotos com marcação</h4>
        <div class="avarias-fotos-grid">
          ${avarias.fotos
            .map(
              (foto) => `
            <div class="avaria-foto-frame">
              <img src="${foto.dataUrl}" alt="Foto de avaria" />
              ${marcacoesHtml(foto)}
            </div>
            ${listaMarcacoesHtml(foto)}`
            )
            .join("")}
        </div>`
      : "";

  const totalAvarias = avarias.diagrama.length + avarias.fotos.reduce((t, f) => t + f.pontos.length, 0);
  const avariasHtml =
    totalAvarias > 0
      ? `<h3>Avarias e riscos externos</h3>${diagramaHtml}${fotosAvariaHtml}`
      : `<h3>Avarias e riscos externos</h3><p class="vazio">Nenhuma avaria registrada.</p>`;

  const assinaturas = `
    <div class="assinaturas">
      <div class="assinatura-box">
        <p class="label">Mecânico responsável</p>
        ${
          checklist.assinaturaMecanico
            ? `<img src="${checklist.assinaturaMecanico}" alt="assinatura mecânico" />`
            : `<p class="vazio">Não assinado</p>`
        }
      </div>
      <div class="assinatura-box">
        <p class="label">Cliente</p>
        ${
          checklist.assinaturaCliente
            ? `<img src="${checklist.assinaturaCliente}" alt="assinatura cliente" />`
            : `<p class="vazio">Não assinado</p>`
        }
      </div>
    </div>`;

  const titulo = `Checklist — ${checklist.veiculo?.placa || checklist.cliente?.nome || checklist.id}`;

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(titulo)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #1f2937;
    margin: 0;
    padding: 32px;
    max-width: 860px;
    margin: 0 auto;
  }
  header {
    display: flex;
    align-items: center;
    gap: 14px;
    border-bottom: 2px solid #1f2937;
    padding-bottom: 16px;
    margin-bottom: 20px;
  }
  header img { width: 48px; height: 48px; border-radius: 8px; object-fit: cover; }
  header h1 { font-size: 20px; margin: 0; }
  header p { margin: 2px 0 0; color: #6b7280; font-size: 13px; }
  .meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 24px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 20px;
    font-size: 14px;
  }
  .meta-grid strong { color: #374151; }
  h3 { font-size: 15px; margin: 22px 0 8px; color: #111827; }
  table.itens { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 13px; }
  table.itens th {
    text-align: left;
    background: #f3f4f6;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
  }
  table.itens td {
    padding: 6px 8px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: top;
  }
  .status-pill {
    display: inline-block;
    color: #fff;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid transparent;
  }
  .fotos { display: flex; gap: 4px; flex-wrap: wrap; }
  .fotos img.foto { width: 42px; height: 42px; object-fit: cover; border-radius: 4px; border: 1px solid #e5e7eb; }
  .observacoes { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; font-size: 14px; margin-top: 8px; }
  .assinaturas { display: flex; gap: 24px; margin-top: 24px; }
  .assinatura-box { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center; }
  .assinatura-box img { max-width: 100%; max-height: 100px; }
  .assinatura-box .label { font-size: 12px; color: #6b7280; margin: 0 0 6px; }
  .assinatura-box .vazio { color: #9ca3af; font-size: 13px; }
  h4 { font-size: 13px; margin: 14px 0 6px; color: #374151; }
  .avarias-lista { list-style: none; margin: 0 0 10px; padding: 0; font-size: 13px; }
  .avarias-lista li { padding: 4px 0; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; gap: 6px; }
  .avarias-lista--foto { margin-top: 4px; margin-bottom: 16px; }
  .avaria-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; border: 1px solid transparent; }
  /* align-items: start e OBRIGATORIO. O quadro da foto e a lista de texto
     sao irmaos no mesmo grid; com o "stretch" padrao, o quadro estica ate a
     altura da lista e TODA marcacao posicionada em % cai contra a caixa
     esticada em vez da imagem -- os pinos saem flutuando abaixo da foto. */
  .avarias-fotos-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 16px; align-items: start; }
  .avaria-foto-frame { position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; line-height: 0; }
  .avaria-foto-frame img { width: 100%; height: auto; display: block; }
  .avaria-svg { position: absolute; left: 0; top: 0; width: 100%; height: 100%; pointer-events: none; }
  .avaria-marker {
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #fff;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translate(-50%, -50%);
    box-shadow: 0 1px 4px rgba(0,0,0,0.4);
  }
  footer { margin-top: 28px; font-size: 11px; color: #9ca3af; text-align: center; }
  @media print {
    body { padding: 0 18px; }
    .assinatura-box { break-inside: avoid; }
    table.itens { break-inside: auto; }
    tr { break-inside: avoid; }

    /* O dialogo de impressao descarta cor de fundo por padrao ("graficos de
       segundo plano" desmarcado). Sem isto, a pilula de status vira texto
       branco sobre branco e o documento sai com as caixinhas em branco.
       Aplicado SO nas marcacoes: print-color-adjust e herdado, entao os
       fundos cinza (meta-grid, cabecalho da tabela, observacoes) continuam
       sendo descartados -- cor nas marcacoes, nao no fundo do documento. */
    .status-pill,
    .avaria-dot,
    .avaria-marker,
    .avaria-svg {
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
      print-color-adjust: exact;
    }
  }
</style>
</head>
<body>
  <header>
    ${logoUrl ? `<img src="${logoUrl}" alt="logo" />` : ""}
    <div>
      <h1>${escapeHtml(workshopName || "Oficina")}</h1>
      <p>Checklist de inspeção veicular · gerado em ${formatData(new Date().toISOString())}</p>
    </div>
  </header>

  <div class="meta-grid">
    <div><strong>Cliente:</strong> ${escapeHtml(checklist.cliente?.nome) || "—"}</div>
    <div><strong>Telefone:</strong> ${escapeHtml(checklist.cliente?.telefone) || "—"}</div>
    <div><strong>Veículo:</strong> ${escapeHtml(checklist.veiculo?.marca)} ${escapeHtml(checklist.veiculo?.modelo)} (${escapeHtml(checklist.veiculo?.ano) || "—"})</div>
    <div><strong>Placa:</strong> ${escapeHtml(checklist.veiculo?.placa) || "—"}</div>
    <div><strong>KM:</strong> ${escapeHtml(checklist.veiculo?.km) || "—"}</div>
    <div><strong>Mecânico:</strong> ${escapeHtml(checklist.mecanicoResponsavel) || "—"}</div>
    <div><strong>Status:</strong> ${escapeHtml(CHECKLIST_STATUS_LABELS[checklist.status] || checklist.status)}</div>
    <div><strong>Última atualização:</strong> ${formatData(checklist.updatedAt)}</div>
  </div>

  ${itensHtml}

  ${fotosGeraisHtml}

  ${avariasHtml}

  ${
    checklist.observacoesGerais
      ? `<h3>Observações gerais</h3><div class="observacoes">${escapeHtml(checklist.observacoesGerais)}</div>`
      : ""
  }

  ${assinaturas}

  <footer>Documento gerado automaticamente pelo módulo de checklist da oficina.</footer>
</body>
</html>`;
}

export function downloadChecklistHtml(checklist, options) {
  const html = buildChecklistDocument(checklist, options);
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const placa = checklist.veiculo?.placa ? `-${checklist.veiculo.placa}` : "";
  a.href = url;
  a.download = `checklist${placa}-${checklist.id}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function printChecklistAsPdf(checklist, options) {
  const html = buildChecklistDocument(checklist, options);
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}
